// `search.page.tpl.html:323` — the criteria builder's second section: one row per skill group,
// each with its skills, a years range and a "last used".
//
// ⚠️ THE SKILLS CELL HAS TWO FORMS THAT LOOK IDENTICAL AND ARE NOT. Upstream writes the
// `tags-input` twice, under `generic-search` and `non-generic-search`, switched on
// `canViewGenericSearch()`. The markup is the same; three things differ:
//
//   - the autocomplete source (`getExtensionSkills($query)` against `…($query, true)`);
//   - its template (`autocomplete-sans-freetext-prefix` against `skills-with-demand`, which is
//     what draws the flame on an in-demand skill);
//   - the tag-added handler, which in the generic case passes the tag on for a follow-up call.
//
// Collapsing them into one input — which the identical markup invites — loses the in-demand
// marker for non-generic users and sends the wrong query for generic ones. Both wrappers are
// kept, because the CLASS on each is also what the stylesheet targets.
//
// ⚠️ AND THE OPERATOR BETWEEN ROWS IS HARDCODED. The field-of-expertise section prints
// `logicalOperatorClauseText[fieldOfExpertiseOperator]`, which the user chooses; here it is
// `logicalOperatorClauseText[0]` — index 0, always "AND". Skills are always ANDed and there is
// no radio group for them, so reading the operator from state here would be a behaviour this
// port invented.
import { useMemo, type ReactElement } from 'react'
import { TagsInput, type Tag } from '../common/TagsInput.tsx'
import { skillsCriteriaValid } from './criteriaValidity.ts'
import {
  LAST_USED_OPTIONS,
  LOGICAL_OPERATOR_CLAUSE,
  LogicalOperator,
  MAX_YEARS_OPTIONS,
  MIN_YEARS_OPTIONS,
  type LastUsedPeriod,
} from './criteriaOptions.ts'

/** `ITempExtensionSkillCriteria`. */
export interface SkillCriteriaRow {
  skills?: Tag[]
  minYears?: number | null
  maxYears?: number | null
  lastUsed?: LastUsedPeriod
  /**
   * ⚠️ CARRIED BUT NOT DRAWN YET. It toggles a two-icon stack beside the years inputs
   * (`search.page.tpl.html:419`) that this section has not ported. It is in the type so a
   * saved alert round-trips it — dropping it on load would silently clear the setting the
   * moment save lands, which is a data-loss bug with no visible symptom until then.
   */
  allowUnknownYears?: boolean
}

export interface SkillSetSectionProps {
  /** `typeaheads.getExtensionSkills($query)`. */
  loadSkills?: (query: string) => Promise<readonly Tag[]>
  rows: readonly SkillCriteriaRow[]
  /** `ctrl.canViewGenericSearch()` — picks which of the two inputs renders. */
  canViewGenericSearch?: boolean
  /** Suggestions for the row being typed in, by row index. */
  suggestions?: Readonly<Record<number, readonly Tag[]>>
  onQueryChange?: (index: number, query: string) => void
  onChange?: (index: number, row: SkillCriteriaRow) => void
  onRemove?: (index: number) => void
  onAdd?: () => void
}

/**
 * `getMultipleTagsInputPlaceholderText` (search.page.component.ng1.ts:2000).
 *
 * ⚠️ THE PLACEHOLDER CHANGES ONCE THERE IS A TAG, and the second form is just the NOUN.
 * Empty it reads "Add skill"; with tags in the field it reads "skill", which alongside the
 * `or-in-between` class is what makes the row read "java OR skill". I first wrote "or" there,
 * which puts the conjunction in twice.
 */
export function tagsPlaceholder(tags: readonly Tag[] | undefined, noun: string, empty?: string): string {
  return tags === undefined || tags.length === 0 ? (empty ?? `Add ${noun}`) : noun
}

export function SkillSetSection({
  rows,
  canViewGenericSearch = false,
  suggestions = {},
  onQueryChange,
  onChange,
  onRemove,
  onAdd,
  loadSkills,
}: SkillSetSectionProps): ReactElement {
  // ⚠️ VALIDITY IS EVENT-DRIVEN UPSTREAM, NOT CONTINUOUS, and computing it on every render
  // made this label appear where production shows none. `$setValidity` is called from
  // `addSkillCriteria()` and the tag handlers — never from the select's `ng-change` — so changing a row's dropdown
  // leaves the previous verdict standing. Keying the memo to the tag counts and the row count
  // reproduces that: a select change does not move the signature, so it does not revalidate.
  const validitySignature = rows.map((row) => row.skills?.length ?? 0).join(',')
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the signature IS the dependency; see above.
  const sectionValid = useMemo(() => skillsCriteriaValid(rows), [validitySignature])
  return (
    <div className="criteria-table">
      <div className="criteria-row">
        <div className="criteria-cell-label">
          <label className="bold-soft">Skills</label>
        </div>
        <div className="criteria-cell-label">
          <label className="bold-soft">Years of experience</label>
        </div>
        <div className="criteria-cell-label">
          <label className="bold-soft">Last used</label>
        </div>
      </div>

      {rows.map((row, index) => (
        // eslint-disable-next-line react/no-array-index-key -- positional upstream too.
        <div className="skills-criteria sub-section criteria-row" key={index}>
          <div className="skills-input criteria-cell">
            <div className={canViewGenericSearch ? 'generic-search' : 'non-generic-search'}>
              <TagsInput
                className="tags-input-default-width or-in-between"
                name={`skills${String(index)}`}
                placeholder={tagsPlaceholder(row.skills, 'skill')}
                tags={row.skills ?? []}
                indicator
                indicatorClassName="fas fa-search"
                suggestions={suggestions[index] ?? []}
                {...(loadSkills === undefined ? {} : { loadSuggestions: loadSkills })}
                onQueryChange={(query) => onQueryChange?.(index, query)}
                onAdd={(tag) => onChange?.(index, { ...row, skills: [...(row.skills ?? []), tag] })}
                onRemove={(at) =>
                  onChange?.(index, { ...row, skills: (row.skills ?? []).filter((_, i) => i !== at) })
                }
              />
            </div>
            {(row.skills?.length ?? 0) > 0 && index < rows.length - 1 ? (
              <div className="mT10">
                {/* Index 0 — always AND. See the note at the top. */}
                <label className="bold-soft logical-operator-clauses">
                  {LOGICAL_OPERATOR_CLAUSE[LogicalOperator.All]}
                </label>
              </div>
            ) : null}
            {/* `search.page.tpl.html:384` — shown on a row with no skills once the SECTION is
                invalid, which is why a blank new form stays quiet. Production refuses to save
                while it stands; this port showed nothing and left the Save button looking
                broken. The `leftoverText` half of upstream's condition is not ported: it
                needs the typeahead's uncommitted text, which `TagsInput` does not expose. */}
            {(row.skills?.length ?? 0) === 0 && !sectionValid ? (
              <label className="control-label has-error">Please fill in skills from the list</label>
            ) : null}
          </div>

          <div className="skills-years criteria-cell">
            <div className="yoe-dropdown">
              <div className="select min-years-dropdown">
                <select
                  name="minSkillYoe"
                  className="form-control input"
                  value={row.minYears ?? ''}
                  onChange={(event) =>
                    onChange?.(index, {
                      ...row,
                      minYears: event.target.value === '' ? null : Number(event.target.value),
                    })
                  }
                >
                  {MIN_YEARS_OPTIONS.map((option) => (
                    <option key={option.display} value={option.value ?? ''}>
                      {option.display}
                    </option>
                  ))}
                </select>
              </div>
              <div className="select max-years-dropdown">
                <select
                  name="maxSkillYoe"
                  className="form-control input"
                  value={row.maxYears ?? ''}
                  onChange={(event) =>
                    onChange?.(index, {
                      ...row,
                      maxYears: event.target.value === '' ? null : Number(event.target.value),
                    })
                  }
                >
                  {MAX_YEARS_OPTIONS.map((option) => (
                    <option key={option.display} value={option.value ?? ''}>
                      {option.display}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="skills-period criteria-cell">
            <div className="select last-period-dropdown">
              {/* No `name` upstream on this one, unlike its field-of-expertise twin. */}
              <select
                className="form-control input"
                value={row.lastUsed ?? ''}
                onChange={(event) =>
                  onChange?.(index, { ...row, lastUsed: Number(event.target.value) as LastUsedPeriod })
                }
              >
                {LAST_USED_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.display}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div
            className="remove-criteria-container criteria-cell"
            role="button"
            aria-label="Remove"
            tabIndex={0}
            onClick={() => onRemove?.(index)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return
              event.preventDefault()
              onRemove?.(index)
            }}
          >
            <div className="fa-stack fa-1x icons-container">
              <i className="fas fa-circle fa-stack-1x x-icon-container fa-lg" />
              <i className="fal fa-times fa-stack-1x cursor-pointer fa-xs" />
            </div>
          </div>
        </div>
      ))}

      {/* Two placeholders here, not three: this grid has one column fewer than the
          field-of-expertise one, and the count is per section rather than shared. */}
      <div className="criteria-row bottom-cta-row sub-section">
        <div className="criteria-cell placeholder-cell" />
        <div className="criteria-cell placeholder-cell" />
        <div className="criteria-cell skills-add-btn">
          <a
            href=""
            className="add-button"
            onClick={(event) => {
              event.preventDefault()
              onAdd?.()
            }}
          >
            <i className="fal fa-plus-circle" />
            <span>&nbsp;Add</span>
          </a>
        </div>
      </div>
    </div>
  )
}
